/**
 * Pure helpers for the ME report's "CONTEXTO DO RELATÓRIO" selection
 * (Cliente → Projeto → Unidade/Área → Equipamento → Inspeção).
 *
 * Extracted from the page component so the filtering and the
 * context → report seeding can be exercised directly by tests, which cannot
 * instantiate a Web Component (no DOM in the Node test runner) — same
 * reasoning as engineering-workspace-minuta-actions.ts (Bloco 8).
 */

export interface MeContextSelection {
  clientId: string;
  projectId: string;
  unitAreaId: string;
  equipmentId: string;
  inspectionId: string;
}

export interface MeContextSeed {
  clientId: string;
  clientDisplayName: string;
  /** IDs reais do contexto — o workflow do PLH resolve por eles, nunca pelo rótulo. */
  projectId: string | null;
  unitAreaId: string | null;
  projectContract: string;
  unitArea: string;
  equipmentId: string | null;
  equipmentTag: string;
  equipmentTypeLabel: string;
  inspectionId: string | null;
  inspectorProfessionalId: string | null;
  inspectorName: string;
}

/**
 * Equipment is fetched per client; Projeto/Unidade-Área narrow it further.
 * Equipment with no projectId/unitAreaId recorded is kept — those are legacy
 * records that predate the hierarchy, and hiding them would make their
 * inspections unreachable from this page.
 */
export function filterEquipmentsByContext(
  equipments: readonly any[],
  selection: { projectId?: string; unitAreaId?: string }
): any[] {
  return equipments.filter(eq => {
    if (selection.projectId && eq.projectId && eq.projectId !== selection.projectId) return false;
    if (selection.unitAreaId && eq.unitAreaId && eq.unitAreaId !== selection.unitAreaId) return false;
    return true;
  });
}

/** Missing required context fields, phrased for a specific user-facing message (never a generic "dados inválidos"). */
export function missingMeContextFields(selection: MeContextSelection): string[] {
  const missing: string[] = [];
  if (!selection.clientId) missing.push("o Cliente");
  if (!selection.projectId) missing.push("o Projeto");
  if (!selection.unitAreaId) missing.push("a Unidade / Área");
  if (!selection.equipmentId) missing.push("o Equipamento / TAG");
  /**
   * A INSPEÇÃO É VÍNCULO OPCIONAL NO ME.
   *
   * Exigi-la aqui impedia criar o relatório quando a medição de espessura
   * acontece fora de uma Inspeção cadastrada — que é o caso comum de um
   * acompanhamento avulso ou de uma medição pedida pelo Cliente. A saída
   * prática era cadastrar uma Inspeção fictícia só para destravar a tela,
   * poluindo o histórico com inspeções que nunca existiram.
   *
   * O contexto mínimo do ME é Cliente → Projeto → Unidade/Área →
   * Equipamento/TAG. `inspectionId` aceita `null`, e a Inspeção pode ser
   * vinculada depois, se vier a existir.
   */
  return missing;
}

/**
 * Maps the selected cadastro records onto the ME report's identification
 * fields, so section 01 is filled from real registrations instead of being
 * retyped. Nothing is invented: every value comes from a selected record.
 */
/**
 * Selected ids that are no longer present in their (re)loaded option list.
 * A selection may only be acted on when every id still exists in the list the
 * user is actually seeing — otherwise the page could create a report from a
 * context the dropdowns no longer show.
 */
export function staleMeContextFields(
  selection: MeContextSelection,
  lists: {
    clients?: readonly any[];
    projects?: readonly any[];
    unitAreas?: readonly any[];
    equipments?: readonly any[];
    inspections?: readonly any[];
  }
): string[] {
  const stale: string[] = [];
  const absent = (id: string, list?: readonly any[]) => !!id && !!list && !list.some(item => item.id === id);

  if (absent(selection.clientId, lists.clients)) stale.push("o Cliente");
  if (absent(selection.projectId, lists.projects)) stale.push("o Projeto");
  if (absent(selection.unitAreaId, lists.unitAreas)) stale.push("a Unidade / Área");
  if (absent(selection.equipmentId, lists.equipments)) stale.push("o Equipamento / TAG");
  if (absent(selection.inspectionId, lists.inspections)) stale.push("a Inspeção");
  return stale;
}

export function buildMeSeedFromContext(input: {
  selection: MeContextSelection;
  client?: any;
  project?: any;
  unitArea?: any;
  equipment?: any;
  inspection?: any;
  inspector?: any;
  equipmentTypeLabels?: Record<string, string>;
}): MeContextSeed {
  const { selection, client, project, unitArea, equipment, inspection, inspector } = input;
  const labels = input.equipmentTypeLabels ?? {};

  return {
    clientId: selection.clientId,
    clientDisplayName: client ? (client.name || client.legalName || "") : "",
    projectId: selection.projectId || null,
    unitAreaId: selection.unitAreaId || null,
    projectContract: project ? `${project.code} — ${project.name}` : "",
    unitArea: unitArea ? `${unitArea.code} — ${unitArea.name}` : "",
    equipmentId: selection.equipmentId || null,
    equipmentTag: equipment?.tag || "",
    equipmentTypeLabel: equipment ? (labels[equipment.equipmentType] || equipment.equipmentType) : "",
    inspectionId: selection.inspectionId || null,
    inspectorProfessionalId: inspection?.inspectorId || null,
    inspectorName: inspector?.name || ""
  };
}
